import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  useBridgeAPI,
  useBridgeState,
  EventName,
  Request,
  useWebViewPostMessage,
} from '@digitaz/bridge-sdk-client';
import { TFunction } from 'i18next';
import { Input } from '@/components/UI/Input/Input';
import { Chip } from '@/components/UI/Chip/Chip';
import { PaymentMethods } from '@/components/Payment/PaymentMethods';
import {
  CreatePaymentResponse,
  PaymentMethod,
  PaymentService,
  PaymentStatus,
  TerminalPaymentTargetType,
} from '@/api/types/payments.types';
import * as S from '@/components/Payment/AddBalance.styles';
import {
  amounts,
  MAX_AMOUNT,
  MIN_AMOUNT,
  PORTMANAT_MAX_AMOUNT,
} from '@/components/Payment/AddBalance.constants';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { AdService } from '@/api/types/services.types';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { mapPaymentDataToUrl } from '@/views/Wallet/Finish.helpers';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';
import { PaymentInTerminals } from '@/components/Payment/PaymentInTerminals';
import { useTerminalPayment } from '@/components/Payment/useTerminalPayment';
import { useCreatePayment } from '@/hooks/useCreatePayment';
import { MutationError } from '@/api/types/common.types';
import { useNewWindow } from '@/hooks/useNewWindow';
import { usePostMessage } from '@/hooks/usePostMessage';
import { AdBundle } from '@/api/types/categories.types';
import { usePaymentStatusModalStore } from '@/store/usePaymentStatusModalStore';
import { buildWebViewAddBalancePaymentData } from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal.helpers';
import { sleep } from '@/utils/helpers';
import {
  getPaymentYigimTemplateGroup,
  getRefetchDelay,
} from '@/api/helpers/payments.helpers';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { PaymentStatusParams } from '@/bridge/types/payment';
import { ModalDrawer } from '@/components/UI/ModalDrawer';
import { Button } from '@/components/UI/Button/Button';
import { InfoDrawer } from '@/components/Ad/Promotions/InfoDrawer';
import { DeviceType, getDeviceType } from '@/utils/getDeviceType';

export const addBalanceFormSchema = (t: TFunction, payment: PaymentMethod) =>
  z
    .object({
      amount: z.string().min(1, t('common.required_field')),
    })
    .superRefine(({ amount }, ctx) => {
      if (
        (payment === PaymentMethod.Portmanat &&
          Number(amount) > PORTMANAT_MAX_AMOUNT) ||
        Number(amount) > MAX_AMOUNT
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('pages.cabinet.add_amount_max', {
            max:
              payment === PaymentMethod.Portmanat
                ? PORTMANAT_MAX_AMOUNT
                : MAX_AMOUNT,
          }),
          path: ['amount'],
        });
      }
      if (Number(amount) < MIN_AMOUNT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('pages.cabinet.add_amount_min', { min: MIN_AMOUNT }),
          path: ['amount'],
        });
      }
    });

export type AddBalanceFormFields = z.infer<
  ReturnType<typeof addBalanceFormSchema>
>;

export type AddBalanceProps = {
  isOpen: boolean;
  toggle: (v?: boolean) => void;
  service?: AdService | null;
  bundle?: AdBundle | null;
  isInPortal?: boolean;
  onSuccess?: () => void;
  onBalanceTopUpSuccess?: () => void;
  isAppleGooglePayEnabled?: boolean;
  paymentMethods?: PaymentMethod[];
};

export const AddBalance = ({
  isOpen,
  toggle,
  service,
  bundle,
  isInPortal,
  onSuccess,
  onBalanceTopUpSuccess,
  isAppleGooglePayEnabled,
  paymentMethods,
}: AddBalanceProps) => {
  const { t } = useTranslation();
  const {
    newWindow: paymentWindow,
    close: closePaymentWindow,
    open: openPaymentWindow,
  } = useNewWindow();
  const source = usePlatformSource();
  const openWebViewPaymentModal = usePaymentStatusModalStore(
    state => state.openWebViewPaymentModal
  );

  const [payment, setPayment] = useState(PaymentMethod.My_card);
  const [cardId, setCardId] = useState<string | undefined>(undefined);
  const [errorState, setErrorState] = useState({
    message: '',
    isOpen: false,
  });

  const { isWebView } = useBridgeState();
  const bridgeAPI = useBridgeAPI();

  const userAgent = window.navigator.userAgent || '';
  const { deviceType } = getDeviceType(userAgent);
  const isDesktop = deviceType === DeviceType.Desktop;

  const { isTerminalOpen, toggleTerminal, isTerminal } = useTerminalPayment({
    payment,
  });

  const {
    createPayment,
    loading: isPaymentLoading,
    paymentProvider,
  } = useCreatePayment({
    onSuccess: onSuccessPayment,
    onError: onCreatePaymentError,
    onRedirect: onCreatePaymentRedirect,
  });

  function handleWebViewPostMessage(
    params: Request.Params[EventName.PaymentStatus]
  ) {
    if (!isOpen) return;

    const delay = getRefetchDelay(paymentProvider);
    const paymentParams = params as unknown as PaymentStatusParams;

    openWebViewPaymentModal(paymentParams);

    if (paymentParams.status === PaymentStatus.Success) {
      toggle(false);

      sleep(delay).then(() => {
        refetchUserPaymentInfo();
        onBalanceTopUpSuccess?.();
      });
    }
  }

  function handlePostMessage() {
    if (!isOpen || payment !== PaymentMethod.Card) return;

    const delay = getRefetchDelay(paymentProvider);

    sleep(delay).then(() => {
      refetchUserPaymentInfo();
      onBalanceTopUpSuccess?.();
      onSuccess?.();
      toggle(false);
    });
  }

  useWebViewPostMessage({
    eventName: EventName.PaymentStatus,
    handler: handleWebViewPostMessage,
  });

  usePostMessage({
    postMessageName: 'paymentFinished',
    handler: handlePostMessage,
  });

  function closeErrorDrawer() {
    setErrorState(prev => ({ ...prev, isOpen: false }));
    setTimeout(
      () => setErrorState(prev => ({ ...prev, message: '' })),
      MODAL_ANIMATION_DURATION
    );
  }

  const { control, handleSubmit, setValue, watch, trigger, resetField } =
    useForm<AddBalanceFormFields>({
      resolver: zodResolver(addBalanceFormSchema(t, payment)),
      defaultValues: { amount: amounts[1].toString() },
      mode: 'onChange',
    });

  const watchedAmount = watch('amount');

  const { refetch: refetchUserPaymentInfo } = useQuery<GetUserResponse>(
    GET_USER,
    { skip: true }
  );

  function onCreatePaymentError(
    status: PaymentStatus,
    errors?: MutationError[]
  ) {
    closePaymentWindow();
    if (errors?.length && status === PaymentStatus.Failure) {
      const isDuplicate = errors[0].code === '666';
      if (isWebView) {
        if (isDuplicate) {
          toggle(false);
          openWebViewPaymentModal({ status: PaymentStatus.Duplicate });
        } else {
          openWebViewPaymentModal({ status: PaymentStatus.Failure });
        }
      } else {
        setErrorState(prev => ({
          ...prev,
          isOpen: true,
          message: errors[0].message,
        }));
        if (isDuplicate) {
          toggle(false);
        }
      }
    }
  }
  function onCreatePaymentRedirect(redirectUrl: string) {
    if (isWebView) {
      bridgeAPI.openPayment(redirectUrl);
      return;
    }

    if (paymentWindow) {
      paymentWindow.location.href = redirectUrl;
    }
  }

  function onSuccessPayment(data: CreatePaymentResponse) {
    const { paymentDate, paymentLegacyId, targetLegacyId } =
      data.createPayment.entity;
    if (paymentDate && paymentLegacyId && targetLegacyId) {
      setPayment(PaymentMethod.Wallet);
      const url = mapPaymentDataToUrl({
        id: paymentLegacyId,
        price: watchedAmount.toString(),
        date: paymentDate,
        paymentMethod: payment,
        privateAccountId: targetLegacyId,
      });

      if (url && !isWebView) {
        if (paymentWindow) {
          paymentWindow.location.href = url;
        }
        refetchUserPaymentInfo();
        onBalanceTopUpSuccess?.();
        onSuccess?.();
        toggle(false);

        return;
      }

      if (isWebView) {
        const params = buildWebViewAddBalancePaymentData(t, {
          paymentDate,
          paymentId: paymentLegacyId,
          targetId: targetLegacyId,
          amount: watchedAmount,
        });
        openWebViewPaymentModal(params);
        refetchUserPaymentInfo();
        onBalanceTopUpSuccess?.();
        toggle(false);
        return;
      }

      closePaymentWindow();
    }
  }

  function onSubmit(data: AddBalanceFormFields) {
    if (isTerminal) {
      return toggleTerminal();
    }

    createPayment({
      variables: {
        serviceId: PaymentService.Add_balance_custom,
        paymentMethod: payment,
        amount: Number(data.amount),
        yigimTemplateGroup: getPaymentYigimTemplateGroup(
          isDesktop,
          isAppleGooglePayEnabled
        ),
        source,
        ...(payment === PaymentMethod.My_card && cardId && { cardId }),
      },
    });

    if (!isWebView) openPaymentWindow();
  }

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(
        () => resetField('amount'),
        MODAL_ANIMATION_DURATION
      );
      return () => clearTimeout(timer);
    }

    trigger('amount');
  }, [isOpen, payment]);

  useEffect(() => {
    if (!isOpen) return;

    const defaultValue = amounts[1].toString();

    if (service) {
      setValue('amount', service.price.toString());
      return;
    }

    if (bundle) {
      setValue('amount', bundle.price.toString());
      return;
    }

    setValue('amount', defaultValue);
  }, [isOpen, service, bundle]);

  return (
    <ModalDrawer
      isInPortal={isInPortal}
      dataTestId={AdPageTestIds.addBalance.window}
      isOpen={isOpen}
      onClose={() => toggle(false)}
      title={t('pages.cabinet.add_private_account')}
      bottom={
        <Button
          data-testid={AdPageTestIds.addBalance.button}
          data-stat={
            payment === PaymentMethod.My_card
              ? 'topup-pay-with-token'
              : undefined
          }
          appearance="secondary"
          isFullWidth
          borderRadius={14}
          onClick={handleSubmit(onSubmit)}
          disabled={isPaymentLoading}
        >
          {t('common.add_balance_btn')}
        </Button>
      }
    >
      <S.Container>
        <S.Form noValidate>
          <Controller
            name="amount"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                isNumber
                isBigText
                precision={2}
                appearance="filled"
                label={t('pages.cabinet.add_amount')}
                helperText={fieldState.error?.message}
                hasError={!!fieldState.error}
              />
            )}
          />

          <S.Chips>
            {amounts.map(amount => (
              <Chip
                key={amount}
                appearance={
                  watchedAmount === amount.toString() ? 'primary' : 'gray'
                }
                isRounded
                onClick={() =>
                  setValue('amount', amount.toString(), {
                    shouldValidate: true,
                  })
                }
              >
                {amount} ₼
              </Chip>
            ))}
          </S.Chips>
        </S.Form>

        <PaymentMethods
          payment={payment}
          setPayment={setPayment}
          setCardId={setCardId}
          cardId={cardId}
          isAddBalanceOpen={isOpen}
          paymentMethods={paymentMethods}
          isAppleGooglePayEnabled={isAppleGooglePayEnabled}
        />
        <S.PaymentAgreementText
          buttonName={t('common.add_balance_agreement')}
        />
      </S.Container>

      <InfoDrawer
        isError
        dataTestId={AdPageTestIds.addBalance.errorDrawer}
        isOpen={errorState.isOpen}
        onClose={closeErrorDrawer}
        description={errorState.message}
      />

      <PaymentInTerminals
        isOpen={isTerminalOpen}
        toggle={toggleTerminal}
        onOk={() => toggleTerminal(false)}
        paymentTargetType={TerminalPaymentTargetType.Balance}
      />
    </ModalDrawer>
  );
};
